import React from 'react';
import { motion } from 'framer-motion';
import { 
  HiOutlineTrendingUp, 
  HiOutlineTrendingDown, 
  HiOutlineMinus,
  HiOutlineTag,
  HiOutlineClock,
  HiOutlineChartBar
} from 'react-icons/hi';
import { SentimentData, getSentimentColor } from '../types/analysis';

// Type the icons properly for TypeScript
const TrendingUpIcon = HiOutlineTrendingUp as React.ComponentType<{ className?: string }>;
const TrendingDownIcon = HiOutlineTrendingDown as React.ComponentType<{ className?: string }>;
const MinusIcon = HiOutlineMinus as React.ComponentType<{ className?: string }>;
const TagIcon = HiOutlineTag as React.ComponentType<{ className?: string }>;
const ClockIcon = HiOutlineClock as React.ComponentType<{ className?: string }>;
const ChartBarIcon = HiOutlineChartBar as React.ComponentType<{ className?: string }>;

interface SentimentAnalysisCardProps {
  sentimentData: { [key: string]: SentimentData };
  className?: string;
}

const SentimentAnalysisCard: React.FC<SentimentAnalysisCardProps> = ({ 
  sentimentData, 
  className = '' 
}) => {
  if (!sentimentData || Object.keys(sentimentData).length === 0) {
    return null;
  }

  const getSentimentIcon = (label: string) => {
    switch (label.toLowerCase()) {
      case 'bullish':
        return <TrendingUpIcon className="w-5 h-5 text-green-600" />;
      case 'bearish':
        return <TrendingDownIcon className="w-5 h-5 text-red-600" />;
      default:
        return <MinusIcon className="w-5 h-5 text-yellow-600" />;
    }
  };

  const getSentimentBgColor = (label: string) => {
    switch (label.toLowerCase()) {
      case 'bullish':
        return 'bg-green-50 border-green-200';
      case 'bearish':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-yellow-50 border-yellow-200';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Unknown';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`bg-white rounded-xl shadow-sm border p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
          <ChartBarIcon className="w-6 h-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Market Sentiment Analysis</h3>
          <p className="text-sm text-gray-600">Real-time sentiment from news, social media & Reddit</p>
        </div>
      </div>

      {/* Sentiment Data Grid */}
      <div className="space-y-4">
        {Object.entries(sentimentData).map(([symbol, data]) => (
          <motion.div
            key={symbol}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            className={`border rounded-lg p-4 ${getSentimentBgColor(data.sentiment_label)}`}
          >
            {/* Symbol and Sentiment Label */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-2">
                <span className="font-semibold text-gray-900">{data.ticker}</span>
                <span className="text-sm text-gray-500">({symbol})</span>
              </div>
              <div className="flex items-center space-x-2">
                {getSentimentIcon(data.sentiment_label)}
                <span className={`font-semibold ${getSentimentColor(data.sentiment_score)}`}>
                  {data.sentiment_label}
                </span>
              </div>
            </div>

            {/* Sentiment Score Progress Bar */}
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-700">Sentiment Score</span>
                <span className={`text-sm font-semibold ${getSentimentColor(data.sentiment_score)}`}>
                  {data.sentiment_score.toFixed(1)}/100
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${data.sentiment_score}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className={`h-2 rounded-full ${
                    data.sentiment_score >= 55 
                      ? 'bg-green-500' 
                      : data.sentiment_score >= 45 
                      ? 'bg-yellow-500' 
                      : 'bg-red-500'
                  }`}
                />
              </div>
            </div>

            {/* Trending Topics */}
            {data.trending_topics && data.trending_topics.length > 0 && (
              <div className="mb-3">
                <div className="flex items-center space-x-2 mb-2">
                  <TagIcon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">Trending Topics</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {data.trending_topics.slice(0, 5).map((topic, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-white bg-opacity-70 rounded-md text-xs font-medium text-gray-700 border border-gray-200"
                    >
                      {topic}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Metrics */}
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center space-x-2">
                <ClockIcon className="w-4 h-4 text-gray-500" />
                <div>
                  <span className="text-gray-500">Updated</span>
                  <div className="font-medium text-gray-900">
                    {formatDate(data.last_updated)}
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <ChartBarIcon className="w-4 h-4 text-gray-500" />
                <div>
                  <span className="text-gray-500">Sources</span>
                  <div className="font-medium text-gray-900">
                    {data.source_count} analyzed
                  </div>
                </div>
              </div>
            </div>

            {/* Confidence Score */}
            <div className="mt-3 pt-3 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Confidence</span>
                <span className="text-sm font-semibold text-gray-900">
                  {(data.confidence_score * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Footer Note */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500 text-center">
          Sentiment analysis based on real-time data from news, Twitter, and Reddit sources
        </p>
      </div>
    </motion.div>
  );
};

export default SentimentAnalysisCard;
